import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import type { ChipProps } from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import type { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import { Autocomplete } from '@openx/design-system/components/Autocomplete/Autocomplete.tsx';
import { noop } from '@openx/utils/noop.ts';
import { type JSX, useCallback, useState } from 'react';
import { DebounceField } from '../DebounceField/DebounceField.tsx';
import { TextField } from '../TextField/TextField.tsx';
import { useDesignSystemEnabled } from '../theme/useDesignSystemEnabled.ts';
import { AutoCompleteRenderOptions } from './AutoCompleteRenderOptions.tsx';
import {
  AutocompleteDesignSystemScope,
  designSystemChipProps,
  designSystemDefaultProps,
  renderDesignSystemInput,
} from './autocompleteDesignSystem.tsx';
import type { AutocompleteProps, RenderOptions } from './types.ts';

const chipProps: ChipProps = {
  // @ts-expect-error
  // there is no type definition for data-* attributes for ChipProps, although it works and we keep it because it's used for testing
  'data-test': 'chip-button',
  deleteIcon: (
    <IconButton size="large" data-test="chip-delete">
      <CloseIcon />
    </IconButton>
  ),
};

const defaultProps = {
  disabledItemsFocusable: true,
  limitTags: 1,
  multiple: undefined,
  popupIcon: <KeyboardArrowDownIcon />,
};

const dsChipProps = designSystemChipProps('chip-delete');

export function AutoComplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  textFieldProps,
  textFieldReadOnly,
  loading,
  debounced = false,
  onDebounceChange = noop,
  renderOptions,
  designSystem,
  ...props
}: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>): JSX.Element {
  const isDesignSystem = useDesignSystemEnabled('autocomplete', designSystem);
  const [inputValue, setInputValue] = useState('');
  const { field: _field, form: _form, onInputChange, slotProps, ...restProps } = props;

  const TextInputComponent = debounced ? DebounceField : TextField;

  const handleInputChange = useCallback(
    (e, newValue: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'reset') {
        setInputValue('');
      } else {
        setInputValue(newValue);
      }
      onInputChange?.(e, newValue, reason);
    },
    [onInputChange]
  );

  const getOptionValue = useCallback(
    (
      option: T,
      getOptionValue?: RenderOptions<T>['getOptionValue'],
      getOptionLabel?: RenderOptions<T>['getOptionValue']
    ) => {
      if (getOptionValue) return getOptionValue(option);
      if (getOptionLabel) return getOptionLabel(option);

      return typeof option === 'string' ? option : '';
    },
    []
  );

  const getDataTest = useCallback((option: T, dataTest?: string | ((option: T) => string)) => {
    return typeof dataTest === 'string' ? dataTest : dataTest?.(option);
  }, []);

  const hasMultiple = restProps.multiple === true;

  const autocomplete = (
    <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
      {...(isDesignSystem ? designSystemDefaultProps : defaultProps)}
      slotProps={{
        chip: isDesignSystem ? dsChipProps : chipProps,
        ...slotProps,
      }}
      options={props.options || []}
      disableCloseOnSelect={hasMultiple}
      sx={{
        // The DS input is an OutlinedInput, so the legacy `.MuiInput-input` selector would miss it.
        [isDesignSystem ? '& .MuiAutocomplete-input' : '& .MuiInput-input']: {
          cursor: textFieldReadOnly ? 'pointer' : 'auto',
        },
      }}
      renderOption={(state, option, { selected }) => {
        return (
          <li data-test="autocomplete-option" {...state} key={state.key}>
            <AutoCompleteRenderOptions
              option={getOptionValue(option, renderOptions?.getOptionValue, restProps?.getOptionLabel)}
              childrenLocation={renderOptions?.childrenLocation}
              dataTest={getDataTest(option, renderOptions?.dataTest)}
              phraseToHighlight={inputValue}
              getPaddingLevel={() => renderOptions?.getPaddingLevel?.(option)}
              tooltip={renderOptions?.tooltip}
              tooltipPlacement={renderOptions?.tooltipPlacement}
              tooltipMessage={renderOptions?.tooltipMessage?.(option)}
              hasMultiple={hasMultiple}
              selected={selected}
            >
              {renderOptions?.renderChildren?.(option)}
            </AutoCompleteRenderOptions>
          </li>
        );
      }}
      {...(restProps as AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>)}
      onInputChange={handleInputChange}
      renderInput={({ slotProps: paramsSlotProps, ...restParams }) => {
        if (isDesignSystem) {
          return renderDesignSystemInput({
            params: { slotProps: paramsSlotProps, ...restParams },
            textFieldProps,
            debounced,
            onDebounceChange,
            loading,
            textFieldReadOnly,
          });
        }

        return (
          <TextInputComponent
            designSystem={false}
            onChange={onDebounceChange}
            {...restParams}
            {...textFieldProps}
            value={textFieldProps?.value as string | number | undefined}
            slotProps={{
              input: {
                disableUnderline: true,
                ...paramsSlotProps.input,
                ...textFieldProps?.slotProps?.input,
                endAdornment: (
                  <>
                    {loading && (
                      <Box
                        sx={{
                          pr: 1,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                    {textFieldProps?.slotProps?.input &&
                      typeof textFieldProps.slotProps.input === 'object' &&
                      'endAdornment' in textFieldProps.slotProps.input &&
                      textFieldProps.slotProps.input.endAdornment}
                    {paramsSlotProps.input.endAdornment}
                  </>
                ),
                readOnly: textFieldReadOnly,
              },
              inputLabel: { disableAnimation: true, focused: false, shrink: true, ...paramsSlotProps.inputLabel },
              htmlInput: {
                ...paramsSlotProps.htmlInput,
                ...(typeof textFieldProps?.slotProps?.htmlInput === 'object'
                  ? textFieldProps.slotProps.htmlInput
                  : undefined),
              },
            }}
          />
        );
      }}
    />
  );

  return isDesignSystem ? <AutocompleteDesignSystemScope>{autocomplete}</AutocompleteDesignSystemScope> : autocomplete;
}
